import { authenticatedFetch } from '@src/utility/authenticatedFetch';
import { extractErrorInfo } from '@src/utility/error';
import { LogManager } from '@src/utility/Logger';
import { useCallback, useEffect, useRef, useState } from 'react';
import z from 'zod';

import type {
  FaceDetection,
  FaceDetectionsResponse,
  FaceTracks,
  IdentifySpeakersResponse,
} from '@src/services/framing/types';

// Re-export so existing consumers keep working.
export type {
  FaceDetection,
  FaceDetectionsResponse,
  FaceTracks,
  SpeakerIdentifications,
} from '@src/services/framing/types';

const logger = LogManager.getLogger('hook:useFaceDetections');

const FaceDetectionsResponseSchema = z.object({
  detections: z.array(
    z.object({
      id: z.number(),
      time: z.number(),
      box: z.tuple([z.number(), z.number(), z.number(), z.number()]),
      confidence: z.number(),
    }),
  ),
  face_tracks: z.record(z.array(z.number())),
  speakerMapping: z.record(z.string()).optional(),
  speaker_identifications: z.record(z.string()).optional(),
});

const IdentifySpeakersResponseSchema = z.object({
  mode: z.union([z.literal('identify-only'), z.literal('full-detection')]),
  message: z.string(),
  invocationId: z.string().optional(),
  executionArn: z.string().optional(),
  archivedFaceTracks: z.string().optional(),
});

/**
 * Fetch face detections for a story from the API
 * @param storyId - The story ID to fetch detections for
 * @returns Face detections and face tracks, or null if not available
 */
export async function fetchFaceDetectionsApi(
  storyId: string,
  matchWithSpeakers: boolean = false,
): Promise<FaceDetectionsResponse | null> {
  try {
    const response = await authenticatedFetch(
      `${process.env.REACT_APP_API_URL}/api/story/${storyId}/face-detections${
        matchWithSpeakers ? '?matchWithSpeakers=true' : ''
      }`,
      { credentials: 'include' },
    );

    if (!response.ok) {
      return null;
    }

    const data = await response.json();
    const parsed = FaceDetectionsResponseSchema.safeParse(data);
    if (!parsed.success) {
      // Detections that haven't been generated yet come back without
      // `detections` / `face_tracks` — expected, not an error.
      logger.warn('Face detections not available yet', {
        issues: parsed.error.issues,
      });
      return null;
    }
    const parsedData = parsed.data;

    if (parsedData.detections.length === 0) {
      return null;
    }

    // Build label map from face_tracks
    const boxIdLabelMap: { [key: number]: string } = {};
    Object.keys(parsedData.face_tracks).forEach(label => {
      parsedData.face_tracks[label].forEach(id => {
        boxIdLabelMap[id] = label;
      });
    });

    // Map detections with labels
    const detections = parsedData.detections.map(detection => ({
      ...detection,
      label: boxIdLabelMap[detection.id],
    }));

    return {
      detections,
      faceTracks: parsedData.face_tracks,
      speakerMapping: parsedData.speakerMapping || null,
      speakerIdentifications: parsedData.speaker_identifications || null,
    };
  } catch (error) {
    logger.error('Failed to fetch face detections', extractErrorInfo(error));
    return null;
  }
}

/**
 * Saves the story's face → Dato speaker identifications, replacing the
 * override file (`face-identifications-override`). Pass the full map.
 */
export async function saveSpeakerIdentificationsOverrideApi(
  storyId: string,
  map: Record<string, string>,
): Promise<boolean> {
  try {
    const response = await authenticatedFetch(
      `${process.env.REACT_APP_API_URL}/api/story/${storyId}/face-identifications-override`,
      {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ speaker_identifications: map }),
      },
    );
    if (!response.ok) {
      logger.error(
        `face-identifications-override responded with status ${response.status}`,
      );
      return false;
    }
    return true;
  } catch (error) {
    logger.error(
      'Failed to save speaker identifications override',
      extractErrorInfo(error),
    );
    return false;
  }
}

/**
 * Hook for managing face detections for a story
 * Handles fetching, creating, and polling for face detection results
 * @param storyId - The story ID to fetch detections for
 * @param enablePolling - If true, polls every 5 seconds when no detections exist
 */
export const useFaceDetections = (
  storyId: string | undefined,
  enablePolling: boolean = false,
) => {
  const [faceDetections, setFaceDetections] = useState<FaceDetection[] | null>(
    null,
  );
  const [faceTracks, setFaceTracks] = useState<FaceTracks | null>(null);
  const [speakerFaceMapping, setSpeakerFaceMapping] = useState<
    Record<string, string>
  >({});
  const [speakerIdentifications, setSpeakerIdentifications] = useState<
    Record<string, string>
  >({});
  const [loading, setLoading] = useState(false);
  const [detecting, setDetecting] = useState(false);
  const [identifying, setIdentifying] = useState(false);
  const [savingOverride, setSavingOverride] = useState(false);
  // eslint-disable-next-line no-undef
  const pollingIntervalRef = useRef<NodeJS.Timeout | null>(null);

  const fetchFaceDetections = useCallback(async () => {
    if (!storyId) return;

    setLoading(true);
    try {
      const result = await fetchFaceDetectionsApi(storyId, true);
      if (result) {
        setFaceDetections(result.detections);
        setSpeakerFaceMapping(result.speakerMapping || {});
        setSpeakerIdentifications(result.speakerIdentifications || {});
        setFaceTracks(result.faceTracks);
        // Stop polling if we got data
        if (pollingIntervalRef.current) {
          clearInterval(pollingIntervalRef.current);
          pollingIntervalRef.current = null;
          setDetecting(false);
        }
      }
    } catch (error) {
      logger.error('Failed to fetch face detections', extractErrorInfo(error));
    } finally {
      setLoading(false);
    }
  }, [storyId]);

  const createFaceDetections = useCallback(
    async (sceneDetectId?: string) => {
      if (!storyId) return false;

      // Check if detections already exist
      await fetchFaceDetections();
      if (faceDetections && faceDetections.length > 0) {
        return true;
      }

      // No detections exist, create them
      setDetecting(true);
      try {
        const url = sceneDetectId
          ? `${process.env.REACT_APP_API_URL}/api/story/${storyId}/create-face-detections?sceneDetectId=${sceneDetectId}`
          : `${process.env.REACT_APP_API_URL}/api/story/${storyId}/create-face-detections`;

        const response = await authenticatedFetch(url, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          credentials: 'include',
        });

        if (response.ok) {
          logger.info('Face detection started');
          // Back-to-back calls would otherwise leak the prior interval.
          if (pollingIntervalRef.current) {
            clearInterval(pollingIntervalRef.current);
          }
          const interval = setInterval(() => {
            fetchFaceDetections();
          }, 5000);
          pollingIntervalRef.current = interval;
          return true;
        }
        return false;
      } catch (error) {
        logger.error('Failed to start face detection', extractErrorInfo(error));
        setDetecting(false);
        return false;
      }
    },
    [storyId, faceDetections, fetchFaceDetections],
  );

  const runIdentifySpeakers = useCallback(
    async (input: {
      showcaseId?: string;
      forceRerun: boolean;
    }): Promise<IdentifySpeakersResponse | null> => {
      if (!storyId) return null;

      setIdentifying(true);
      try {
        const response = await authenticatedFetch(
          `${process.env.REACT_APP_API_URL}/api/story/${storyId}/identify-speakers`,
          {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            credentials: 'include',
            body: JSON.stringify({
              showcaseId: input.showcaseId,
              forceRerun: input.forceRerun,
            }),
          },
        );

        if (!response.ok) {
          logger.error(
            `identify-speakers responded with status ${response.status}`,
          );
          setIdentifying(false);
          return null;
        }

        const raw = await response.json();
        const parsed = IdentifySpeakersResponseSchema.safeParse(raw);
        if (!parsed.success) {
          logger.warn('identify-speakers response shape mismatch', {
            issues: parsed.error.issues,
          });
        }
        const result: IdentifySpeakersResponse | null = parsed.success
          ? parsed.data
          : null;

        if (result?.mode === 'full-detection') {
          // Full pipeline runs scene detection again; reuse the existing
          // detection polling loop until the GET returns fresh data.
          // `detecting` is the canonical pending flag for full re-runs and
          // is cleared by fetchFaceDetections once results arrive.
          setDetecting(true);
          if (pollingIntervalRef.current) {
            clearInterval(pollingIntervalRef.current);
          }
          pollingIntervalRef.current = setInterval(() => {
            fetchFaceDetections();
          }, 5000);
          setIdentifying(false);
        } else {
          // Fast path: backend awaited the identifier Lambda synchronously,
          // so face-tracks.json is already updated by the time the response
          // arrives. One refetch is sufficient — no polling, no loop.
          await fetchFaceDetections();
          setIdentifying(false);
        }

        return result;
      } catch (error) {
        logger.error(
          'Failed to run identify-speakers',
          extractErrorInfo(error),
        );
        setIdentifying(false);
        return null;
      }
    },
    [storyId, fetchFaceDetections],
  );

  const saveSpeakerIdentificationsOverride = useCallback(
    async (map: Record<string, string>): Promise<boolean> => {
      if (!storyId) return false;

      setSavingOverride(true);
      try {
        const saved = await saveSpeakerIdentificationsOverrideApi(storyId, map);
        if (saved) await fetchFaceDetections();
        return saved;
      } finally {
        setSavingOverride(false);
      }
    },
    [storyId, fetchFaceDetections],
  );

  // Fetch on mount if storyId is available
  useEffect(() => {
    if (storyId) {
      fetchFaceDetections();
    }
  }, [storyId, fetchFaceDetections]);

  // Poll every 5 seconds when enablePolling is true and no detections exist
  useEffect(() => {
    if (!enablePolling || !storyId || faceDetections !== null) {
      return;
    }

    const interval = setInterval(() => {
      if (faceDetections === null) {
        fetchFaceDetections();
      }
    }, 5000);

    return () => {
      clearInterval(interval);
    };
  }, [enablePolling, storyId, faceDetections, fetchFaceDetections]);

  // Cleanup polling on unmount
  useEffect(() => {
    return () => {
      if (pollingIntervalRef.current) {
        clearInterval(pollingIntervalRef.current);
        pollingIntervalRef.current = null;
      }
    };
  }, []);

  return {
    faceDetections,
    speakerFaceMapping,
    speakerIdentifications,
    faceTracks,
    loading,
    detecting,
    identifying,
    savingOverride,
    fetchFaceDetections,
    createFaceDetections,
    runIdentifySpeakers,
    saveSpeakerIdentificationsOverride,
  };
};
